import { maxAttachmentCount } from "api/constants";
import { EAttachmentType } from "api/enums";
import AttachmentModel from "api/models/AttachmentModel";
import MeasurementModel from "api/models/MeasurementModel";
import AttachmentService from "api/services/AttachmentService";
import MeasurementService from "api/services/MeasurementService";
import { lang } from "i18nLang";
import { makeAutoObservable } from "mobx";
import {
  ContractStore,
  MeasurementListStore,
  MeasurementStore,
  MessageStore,
} from "..";

class Store {
  public attachments: AttachmentModel[] = [];
  private attachmentsToExclude: AttachmentModel[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  public reset() {
    this.attachments = [];
    this.attachmentsToExclude = [];
  }

  public async getMeasurementAttachments(measurementId: string) {
    this.reset();

    const measurement = this.findMeasurement(measurementId);
    if (!measurement) return;

    const attachments = await AttachmentService.getAttachmentsByIds(
      measurement.contractUid,
      measurement.attachments
    );
    this.attachments = attachments;
  }

  public hasFileBeingUploaded() {
    return this.attachments.some((att) => att.isUploading());
  }

  public validadeAttachmentsCount(files: File[]) {
    return files.length + this.attachments.length <= maxAttachmentCount;
  }

  public async uploadMeasurementAttachment(file: File) {
    try {
      const contractId = ContractStore.getCurrentContract()?.uid;
      if (!contractId) return;

      await AttachmentService.validateAttachment(file);

      const attachment = AttachmentService.uploadAttachmentToStorage(
        contractId,
        file,
        EAttachmentType.MEASUREMENTS,
        () => {
          MessageStore.error(lang("attachments.errors.uploadError"));
        }
      );
      this.addAttachmentToList(attachment);
    } catch (error: any) {
      MessageStore.error(error.message);
    }
  }

  public async removeAllMeasurementAttachments(measurement: MeasurementModel) {
    const attachments = await AttachmentService.getAttachmentsByIds(
      measurement.contractUid,
      measurement.attachments
    );

    const promises = attachments.map((att) =>
      AttachmentService.removeAttachmentFromContract(
        measurement.contractUid,
        att
      )
    );
    await Promise.all(promises);
  }

  public async removeAllUnsavedMeasurementAttachment() {
    const promises: Promise<void>[] = [];
    const attachments = [...this.attachments];
    for (const attachment of attachments) {
      if (!attachment.isSaved()) {
        promises.push(this.removeMeasurementAttachment(attachment));
      }
    }
    await Promise.all(promises);
  }

  public async removeMeasurementAttachment(attachment: AttachmentModel) {
    const contractId = ContractStore.getCurrentOrCreatedContractId();
    if (!contractId) return;

    if (attachment.isSaved()) {
      this.removeAttachmentFromList(attachment);
      this.attachmentsToExclude.push(attachment);
      return;
    }

    if (attachment.isUploading()) {
      attachment.cancelUpload();
      this.removeAttachmentFromList(attachment);
      return;
    }

    return AttachmentService.removeAttachmentFromStorage(contractId, attachment)
      .then(() => {
        this.removeAttachmentFromList(attachment);
      })
      .catch(() => {
        MessageStore.error(lang("attachments.errors.removeError"));
      });
  }

  public async saveMeasurementAttachments(
    measurementId: string
  ): Promise<void> {
    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    const measurement = this.findMeasurement(measurementId);
    if (!measurement) return;

    const attachments = this.attachments.filter((att) =>
      att.isSuccessfullyUploaded()
    );

    await AttachmentService.applyAttachments(
      contract,
      attachments,
      this.attachmentsToExclude,
      (attUids, transaction) =>
        MeasurementService.updateMeasurementAttachments(
          measurement,
          attUids,
          transaction
        )
    ).catch(() => {
      MessageStore.error(lang("attachments.errors.saveError"));
    });
  }

  public findMeasurement(measurementId: string): MeasurementModel | undefined {
    return measurementId
      ? MeasurementListStore.findMeasurement(measurementId)
      : MeasurementStore.currentMeasurement;
  }

  private addAttachmentToList(attachment: AttachmentModel) {
    this.attachments.push(makeAutoObservable(attachment));
  }

  private removeAttachmentFromList(attachment: AttachmentModel) {
    const index = this.attachments.indexOf(attachment);
    if (index !== -1) {
      this.attachments.splice(index, 1);
    }
  }
}

const MeasurementAttachmentStore = new Store();
export default MeasurementAttachmentStore;
